import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  formatWordPressSyncWarningCount,
  formatWordPressSyncWarningTime,
  getSafeWordPressSyncWarningUrl,
  getWordPressSyncWarningCount,
  getWordPressSyncWarningInstruction,
  getWordPressSyncWarningTargetTypeLabel,
} from './syncWarningsUi';
import type { WordPressPublicationItem, WordPressSyncWarning } from './types';

type Publication = WordPressPublicationItem['publication'] | null | undefined;

export function SyncWarningsListChip(props: {
  publication: Publication;
  sx?: SxProps<Theme>;
}) {
  const { publication } = props;
  if (!publication?.hasActiveSyncWarnings) return null;
  const count = getWordPressSyncWarningCount(publication);
  if (count <= 0) return null;

  const tooltip = (publication.syncWarnings || [])
    .map(warning => {
      const label = warning.fieldLabel || warning.wordpressTargetLabel || warning.wordpressTarget || 'WordPress';
      return warning.values.length > 0 ? `${label}: ${warning.values.join(', ')}` : label;
    })
    .join(' · ');

  return (
    <Tooltip title={`Saknas i WordPress: ${tooltip}`}>
      <Chip
        size="small"
        label={formatWordPressSyncWarningCount(count)}
        color="warning"
        variant="outlined"
        sx={props.sx}
      />
    </Tooltip>
  );
}

function SyncWarningBox(props: { warning: WordPressSyncWarning }) {
  const { warning } = props;
  const actionUrl = getSafeWordPressSyncWarningUrl(warning.actionUrl);
  const heading = warning.fieldLabel || warning.wordpressTargetLabel || warning.wordpressTarget || 'WordPress';
  const wordpressName = warning.wordpressTargetLabel || warning.wordpressTarget;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 1, p: 1.25, bgcolor: 'background.paper' }}>
      <Stack spacing={0.75}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">{heading}</Typography>
            <Chip
              size="small"
              variant="outlined"
              label={getWordPressSyncWarningTargetTypeLabel(warning.resolvedTargetType)}
              sx={{ height: 20, fontSize: '0.68rem' }}
            />
            {wordpressName && wordpressName !== heading && (
              <Typography variant="caption" color="text.secondary">
                I WordPress: {wordpressName}
              </Typography>
            )}
          </Stack>
          {actionUrl && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={actionUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Öppna i WordPress
            </Button>
          )}
        </Box>
        {warning.values.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {warning.values.map(value => (
              <Chip key={value} size="small" color="warning" label={value} sx={{ maxWidth: '100%' }} />
            ))}
          </Stack>
        )}
        <Typography variant="body2" color="text.secondary">
          {getWordPressSyncWarningInstruction(warning)}
        </Typography>
      </Stack>
    </Box>
  );
}

export function SyncWarningsCard(props: { publication: Publication }) {
  const { publication } = props;
  const warnings = publication?.syncWarnings || [];
  if (!publication || warnings.length === 0) return null;
  const updatedAt = formatWordPressSyncWarningTime(publication.syncWarningsUpdatedAt);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'warning.light' }}>
      <Stack spacing={1.25}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography variant="subtitle2">Synkvarningar från WordPress</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatWordPressSyncWarningCount(warnings.length)}{updatedAt ? ` · senaste synk ${updatedAt}` : ''}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {publication.hasActiveSyncWarnings
            ? 'Posten är publicerad. Värdena nedan saknas i WordPress. Varningarna stoppar inte publiceringen.'
            : 'Gäller senaste synken. Posten är inte aktiv i WordPress just nu.'}
        </Typography>
        {warnings.map((warning, index) => (
          <SyncWarningBox key={`${warning.code}-${warning.field}-${warning.wordpressTarget}-${index}`} warning={warning} />
        ))}
        {publication.syncWarningsTruncated && (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Fler varningar finns i WordPress än vad som visas här.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}

export function SyncWarningsPublishNotice(props: { publication: Publication }) {
  const count = getWordPressSyncWarningCount(props.publication);
  if (count <= 0) return null;

  return (
    <Alert severity="warning" sx={{ py: 0.25 }}>
      Senaste synken rapporterade {formatWordPressSyncWarningCount(count)}. Publiceringen stoppas inte. Se synkvarningarna ovan.
    </Alert>
  );
}

export function SyncWarningsBulkLine(props: { itemCount: number }) {
  if (props.itemCount <= 0) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <WarningAmberIcon color="warning" fontSize="small" />
      <Typography variant="body2">
        {props.itemCount} med synkvarningar från senaste synken (publiceras ändå)
      </Typography>
    </Stack>
  );
}
